import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiFolder, FiExternalLink, FiGithub } from 'react-icons/fi';

const ProjectsSection = styled.section`
  padding: 100px 150px;
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const ProjectsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ProjectsTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const ProjectsDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 18px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
`;

const FolderIcon = styled.div`
  color: var(--text-secondary);
  font-size: 40px;
  transition: all 0.3s ease;
`;

const ProjectCard = styled(motion.div)`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${FolderIcon} {
      transform: translateY(-5px);
      color: var(--accent-primary);
    }
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 15px;
  
  a {
    color: var(--text-secondary);
    font-size: 20px;
    transition: color 0.2s ease-in-out;
    
    &:hover {
      color: var(--accent-primary);
    }
  }
`;

const ProjectTitle = styled.h3`
  color: var(--text-primary);
  font-size: 22px;
  margin-bottom: 10px;
  font-weight: 600;
  transition: color 0.2s ease-in-out;
  
  ${ProjectCard}:hover & {
    color: var(--accent-primary);
  }
`;

const ProjectDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 20px;
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: auto;
`;

const TechItem = styled(motion.div)`
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--accent-primary);
  background-color: rgba(100, 255, 218, 0.07);
  padding: 5px 10px;
  border-radius: 3px;
  transition: all 0.2s ease-in-out;
  
  ${ProjectCard}:hover & {
    background-color: rgba(100, 255, 218, 0.1);
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
      duration: 0.5
    }
  }
};

const cardVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const Projects = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [descRef, descInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [gridRef, gridInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const projects = [
    {
      title: "OSINT Search Engine",
      description: "Developed an OSINT search engine that searches leaked databases sourced from the dark web, retrieving associated personally identifiable information (PII) using inputs such as mobile numbers, emails, and other identification numbers.",
      tech: ["ExpressJS", "MongoDB", "Python", "Cloudflare Zero Trust"],
      link: "https://osint.teamlectcoders.in",
      github: null,
      featured: true
    },
    {
      title: "Subdomain Finder",
      description: "Developed a tool that searches Certificate Transparency logs to identify and retrieve subdomains associated with a given domain, aiding in security assessment and reconnaissance phases.",
      tech: ["Cloudflare Pages", "Cloudflare Workers", "Node.js"],
      link: "https://subdomainfinder.in",
      github: null,
      featured: true
    },
    {
      title: "Path Finder",
      description: "Created an internal security tool that identifies vulnerable API endpoints and parameters through automated scanning, significantly improving the efficiency of security assessments.",
      tech: ["Python", "Automation", "Scrapping"],
      link: null,
      github: null,
      featured: false
    },
    {
      title: "Security Automation Scripts",
      description: "Developed Python-based automation tools to enhance Software Development Life Cycle (SDLC) efficiency by automating retrieval of open findings from various security platforms.",
      tech: ["Python", "API Integration", "SDLC"],
      link: null,
      github: null,
      featured: false
    }
  ];
  
  return (
    <ProjectsSection id="projects">
      <ProjectsContainer>
        <ProjectsTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Projects
        </ProjectsTitle>
        
        <ProjectsDescription
          ref={descRef}
          initial={{ y: 20, opacity: 0 }}
          animate={descInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          A selection of security projects I've worked on, focused on enhancing digital security and providing security solutions.
        </ProjectsDescription>
        
        <ProjectsGrid
          ref={gridRef}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={gridInView ? "visible" : "hidden"}
        >
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              whileTap={{ scale: 0.98 }}
            >
              <ProjectHeader>
                <FolderIcon>
                  <FiFolder />
                </FolderIcon>
                <ProjectLinks>
                  {project.github && (
                    <a href={project.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub Repository">
                      <FiGithub />
                    </a>
                  )}
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer" aria-label="Live Project">
                      <FiExternalLink />
                    </a>
                  )}
                </ProjectLinks>
              </ProjectHeader>
              
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              
              <TechList>
                {project.tech.map((tech, i) => (
                  <TechItem key={i}>{tech}</TechItem>
                ))}
              </TechList>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </ProjectsContainer>
    </ProjectsSection>
  );
};

export default Projects; 